import { type BodyStage, NPC_WIDTH_TILES, type PropKind, type SceneProp } from "../render/props";
import {
  type CarriedItem,
  ITEM_BAR,
  ITEM_BOULDER,
  ITEM_CORPSE,
  ITEM_FOOD,
  ITEM_PLANT,
  ITEM_WOOD,
  type JobPost,
  JOB_BURY,
  JOB_CARVE,
  JOB_COOK,
  JOB_FELL,
  JOB_FORGE,
  JOB_HARVEST,
  JOB_INTER,
  JOB_MINE,
  JOB_PATROL,
  JOB_TRANSCRIBE,
  JOB_WRITE,
  type PresentFigure,
  type TerrainSampler,
  type TilePos,
} from "../world/types";
import { surfaceZ } from "./crowd";

/*
 * The job scenes: what a town doing its own work looks like.
 *
 * **The recordless layer's presentation** (Phase 7 plan, the phase entry and
 * O11): a town's daily work is *seen, never logged*, so there is nothing in the
 * chronicle about it and nothing here files anything. What the simulation offers
 * is two lists — the work orders on its board and the bodies it has standing on
 * real ground (`world/types.ts`, {@link WitnessSource.jobs} and
 * {@link WitnessSource.presentFigures}) — and what this module does is turn them
 * into props.
 *
 * It decides **nothing about the world**, which is the line this file is on the
 * right side of by having no arguments it could decide with: it is handed a
 * board and a roster and it answers with boxes. Who is working, what they are
 * working on, where they are standing and how far along they are are all facts
 * that arrived; which tool is drawn at a seam of rock is a rendering choice, the
 * same kind of choice as which way a gravestone faces (`world/wasm.ts`'s
 * `facingOf`).
 *
 * # One owner per body
 *
 * Until wave 1F the client decided where every body stood, because the
 * simulation had no opinion (Phase 3 decision 8). The local tier gives the
 * **promoted settlement's** roster real positions and walks them along real
 * paths, and wave 1F's reconciliation — sim positions where they exist, the
 * ambient loop everywhere else — turned out to be the bug rather than the fix:
 * two systems drawing one roster is a body with two sources, and a promotion or
 * a demotion swapped them under a standing player.
 *
 * Item 6 finished it (ruled 2026-08-28): **a named figure is drawn from
 * {@link WitnessSource.presentFigures} or is not drawn at all.** Unpromoted →
 * not drawn, which is what a town beyond the halo already looked like. The
 * ambient loop keeps its future as the unnamed crowd layer and draws no figures
 * (`witness/crowd.ts`); what is still embodied client-side is what the
 * simulation holds no position for — a caravan on a road, a column of refugees.
 *
 * # The child is a scale, and the scale is the simulation's
 *
 * Chunk I-6b gave the simulation children to embody and one boolean about them
 * — grown or not — and I-6c (ruled 2026-08-29) turned that boolean into a
 * {@link BodyStage} on the prop, here, with two authored numbers behind it that
 * the chunk called a stub and named its own deleter for.
 *
 * **Wave 2A was the deleter.** The body plan is data now — a life-stage table
 * and a size-by-age curve, per race — so this file reads a stage *and* a scale
 * off the simulation and authors neither ({@link WitnessSource.bodyScale};
 * `sim_core::body`). What is still this client's is the *word* on the plate,
 * which is presentation and which no sim table has an opinion about.
 *
 * # The animation is one number
 *
 * A work order carries no progress and never will: *"he is chopping that tree"*
 * is the whole of what a scene needs to know, and a countdown is a number a
 * renderer would have to interpolate to use (`sim_core::JobPost`). So the motion
 * is a **rock about z** applied to the tool prop at the site, driven by the
 * client's own stepped clock and seeded per site so two people working ten tiles
 * apart are not swinging in time. Nothing about it is read back, nothing about
 * it is hashed, and a session that ran with it frozen would be the same session.
 */

/**
 * How far the tool at a work site swings, in radians either way.
 *
 * PROVISIONAL and small on purpose: this is a marker seen across a square, not
 * a body, and a tool that swung through ninety degrees would read as a windmill.
 * A fifth of a radian is about twelve degrees — enough that the eye catches the
 * movement at twenty tiles, little enough that a screenshot of it looks like a
 * tool at rest.
 */
export const TOIL_SWING_RADIANS = 0.2;

/**
 * How long one swing takes, in seconds of stepped time. PROVISIONAL.
 *
 * A working pace rather than a frantic one. It is *stepped* seconds rather than
 * frame seconds, like everything else the witnessing layer animates, so a
 * session being fast-forwarded through a night does not leave a tool hanging
 * mid-swing (`witness/crowd.ts` makes the same call).
 */
export const TOIL_SWING_SECONDS = 1.1;

/**
 * Which tool stands at each kind of work: the {@link PropKind} `toil`'s variant.
 *
 * Six for eleven, and the collapsing is deliberate. The three kinds worked at a
 * *tile* get the tool that does them; the five worked at a *bench* share one
 * bench, because which bench it is, is a fact about the building standing there
 * — which the client reads off that building's own row
 * ({@link WitnessSource.lots}) rather than inferring from a job kind — the
 * watch gets a post of its own, because a guard standing at a gate is not
 * somebody working at anything, and the two halves of a burial share the spade
 * that does both.
 *
 * An id this table does not know is a job kind a later build minted, and it
 * draws the bench: work is happening and the client does not know what kind,
 * which is the honest picture and not a dropped prop.
 */
export function toilVariant(kind: number): number {
  switch (kind) {
    case JOB_MINE:
      return 0;
    case JOB_FELL:
      return 1;
    case JOB_HARVEST:
      return 2;

    // The five worked at a bench, spelled out rather than left to the fallback:
    // a kind that fell through by accident and a kind that belongs here would
    // otherwise be the same line.
    case JOB_FORGE:
    case JOB_CARVE:
    case JOB_COOK:
    case JOB_WRITE:
    case JOB_TRANSCRIBE:
      return 3;

    // The watch's post: a planted spear and a brazier, which is what says
    // *somebody stands here* rather than *somebody works here*.
    case JOB_PATROL:
      return 4;

    // The two halves of a burial share one tool, because it is one tool: a
    // spade lifts a body off the street and a spade opens the ground at the
    // burial ground. Which half it is, is a fact about *where* the spade is
    // standing, and the ground under it says that better than a second prop
    // would.
    case JOB_BURY:
    case JOB_INTER:
      return 5;

    default:
      return 3;
  }
}

/**
 * What somebody at a kind of work is *doing*, in one word.
 *
 * **The debug window's Bodies and Places lists and nothing else** (chunk D2):
 * the tool at a site says what kind of work it is to an eye, and a list of text
 * lines has no tool to show — so the same eleven ids get a word here that they
 * get a variant for above.
 *
 * The **ids** are the simulation's and are guarded against its own vocabulary at
 * startup (`world/wasm.ts`'s `checkLocalTier` checks each id *and* the word
 * `job_kind_name` gives it), so a renumbering or a rename fails loudly on load
 * rather than mislabelling a line. The **gerunds** are this client's, on the
 * same licence the plate's own word has: the simulation says *mine* and a list
 * of people says *mining*, and turning one into the other is English rather than
 * data.
 *
 * An id this table does not know is a kind a later build minted and it reads as
 * plain *working* — {@link toilVariant}'s fallback said in words, and the same
 * honest direction: work is happening and the client cannot name it.
 */
export function workWord(kind: number): string {
  switch (kind) {
    case JOB_MINE:
      return "mining";
    case JOB_FELL:
      return "felling";
    case JOB_HARVEST:
      return "harvesting";
    case JOB_FORGE:
      return "forging";
    case JOB_CARVE:
      return "carving";
    case JOB_COOK:
      return "cooking";
    case JOB_WRITE:
      return "writing";
    case JOB_TRANSCRIBE:
      return "transcribing";
    case JOB_PATROL:
      return "on watch";
    case JOB_BURY:
      return "taking up a body";
    case JOB_INTER:
      return "burying somebody";

    default:
      return "working";
  }
}

/**
 * Which heap is drawn for a kind of item: the {@link PropKind} `goods`'s
 * variant.
 *
 * Four families of material rather than one per kind, because the client has no
 * material table and no business having one. The ids are `sim_core::ItemKind`'s,
 * which are DF's `item_type` numbers with all their gaps — so this is a lookup
 * and never arithmetic (`world/types.ts`).
 *
 * **A body is not on this table**, and the polish pass is what took it off: it
 * answered with a fifth variant that {@link VARIANT_COUNT} folded back onto the
 * first, so every corpse in the world was drawn as a heap of rock. What a body
 * is now is a prop kind of its own ({@link isABody}).
 *
 * A kind this build does not know draws as a crate, which is what *made thing*
 * looks like and is the safe direction: a later build's kinds are things people
 * make.
 */
export function goodsVariant(kind: number): number {
  switch (kind) {
    case ITEM_BAR:
    case ITEM_BOULDER:
      return 0;

    case ITEM_WOOD:
      return 1;

    case ITEM_PLANT:
    case ITEM_FOOD:
      return 2;

    default:
      return 3;
  }
}

/**
 * Whether what is lying on a tile is a body rather than goods.
 *
 * **A body is not a heap**, and the polish pass took it out of the heap
 * vocabulary entirely: it was drawn as a goods pile in a colourway of its own,
 * which is a crate that happens to be a different colour, and what a player has
 * to be able to read across a square is *somebody is lying there*. So a corpse
 * gets its own {@link PropKind} — a figure-length shape on the ground
 * ({@link bodyBoxes}) — and this is the one question that decides it.
 *
 * The id is `sim_core::ItemKind::Corpse`'s and is checked against the
 * simulation's own word at startup (`world/wasm.ts`'s `checkLocalTier` has
 * `[ITEM_CORPSE, "body"]` in its list), which is the same guard every other
 * number this client switches on carries.
 */
export function isABody(kind: number): boolean {
  return kind === ITEM_CORPSE;
}

/** One thing a scene puts on the ground, ready for the prop registry. */
export interface ScenePiece {
  readonly prop: SceneProp;

  /** The tile it stands on, so a caller can offer a verb at it. */
  readonly at: TilePos;
}

/**
 * Turns a board into the tools standing at its sites.
 *
 * **Only the orders somebody has taken.** An open job is a thing the town
 * *wants* done and nobody is doing, and a tool swinging at an untouched seam
 * would be the client staging work that is not happening — which is the same
 * rule the director is held to one tier up (`witness/director.ts`: nothing is
 * fabricated). An open order draws nothing at all and is not a hole: it is a
 * tree nobody has got to yet.
 *
 * `groundAt` is the caller's footing probe, so a tool stands on the surface the
 * player would stand on rather than at the site's own z — a felling job's site
 * is the trunk, a metre above the ground it grew out of.
 *
 * `phase` is stepped seconds, used only to rock the tool; two sites are offset
 * from each other by their own coordinates, so a square of workers does not
 * swing in unison.
 */
export function toilPieces(
  board: readonly JobPost[],
  groundAt: (x: number, y: number) => number,
  phase: number,
): readonly ScenePiece[] {
  const pieces: ScenePiece[] = [];

  for (const post of board) {
    if (post.worker === null) {
      continue;
    }

    const x = post.site.x + 0.5;
    const y = post.site.y + 0.5;
    // The *ground* under the site rather than the site itself: a felling job's
    // site is the trunk, which is a wall standing a metre above the soil it
    // grew out of, so a tool planted at the site's own z would float.
    const z = groundAt(x, y);

    pieces.push({
      prop: {
        // The site is the identity, which is what the simulation says a job's
        // identity is: `(site, kind)` and never a row on the board
        // (`sim_core::jobs`). So a job somebody abandoned and somebody else
        // took is one prop that never blinked.
        id: `toil:${post.site.x}:${post.site.y}:${post.site.z}`,
        kind: "toil",
        x,
        y,
        z,
        facing: swing(post.site, phase),
        // No label. What kind of work it is, is said by the tool; a plate over
        // every seam of rock in a mining town would be a HUD.
        label: null,
        palette: toilVariant(post.kind),
      },
      at: post.site,
    });
  }

  return pieces;
}

/**
 * Turns what is lying on a tile into a heap standing on it.
 *
 * **One prop per tile and not per item**, which is what keeps a dug-out corridor
 * from becoming a hundred drawable objects: a pile is a pile, the first item on
 * it is what a pickup takes (`sim_core::hands` spells that rule once), and what
 * the whole pile is worth reading is the label of that first item.
 *
 * The variant comes off that same first item, so a tile with a log on top of a
 * boulder draws as timber — which is honest, because timber is what taking it
 * would hand over.
 */
export function goodsPiece(
  at: TilePos,
  items: readonly CarriedItem[],
  groundAt: (x: number, y: number) => number,
  /**
   * Whether the simulation says a shelf is standing here
   * ({@link WitnessSource.hasShelf}).
   *
   * **Asked rather than inferred.** The client could compare the first item's
   * kind against a number, and that number would be a copy of a vocabulary the
   * simulation owns — the exact thing the seam exists to prevent. One read
   * answers it, and the read is one a library's tile is asked anyway for the
   * empty-shelf beat.
   */
  shelf: boolean,
): ScenePiece | null {
  const first = items[0];

  if (first === undefined) {
    return null;
  }

  const x = at.x + 0.5;
  const y = at.y + 0.5;
  // **A body outranks the pile it is lying in**, which is the pickup's own rule
  // one layer up (`sim_core::hands` takes the first *portable* handle, and the
  // client draws what a player would walk up to): a street where somebody fell
  // beside a dropped boulder is a street with a body in it.
  const body = items.some((item) => isABody(item.kind));
  const kind: PropKind = shelf ? "shelf" : body ? "body" : "goods";

  return {
    prop: {
      id: `goods:${at.x}:${at.y}:${at.z}`,
      kind,
      x,
      y,
      z: heapZ(at, groundAt(x, y)),
      // A heap has no front, so the facing is a cosmetic turn off the tile's own
      // coordinates: a street of them all square to the grid reads as a texture
      // (`world/wasm.ts`'s `facingOf` makes the same call for a gravestone).
      facing: shelf ? 0 : ((at.x * 7 + at.y * 13) % 32) / 32 * Math.PI * 2 - Math.PI,
      label: null,
      palette: shelf || body ? 0 : goodsVariant(first.kind),
    },
    at,
  };
}

/**
 * Which height a heap standing on one tile is drawn at: the column's ground, or
 * the tile's own floor.
 *
 * **Found by chunk Fx-3 and fixed by Fx-7** (`docs/decisions/devtools.md`
 * §1.10). Every heap in the world was placed at {@link surfaceZ} of its column —
 * the *natural* standing level — so a thing on a mine floor, an upper storey or
 * a rooftop was drawn down in the street, which is outside the interact verb's
 * three tiles and therefore unaddressable. The live proof found it twice by
 * conjuring a blade at the feet of a player standing on something other than the
 * ground.
 *
 * One question decides it: **is this tile the ground under it?** A standing level
 * inside the tile's own course is — flat ground answers the tile's floor exactly,
 * and a ramp or a flight answers somewhere between that floor and the next
 * course up, which is the slope the heap should sit on and the case that must
 * not move. Anything else is a floor somebody built, and there the heap belongs
 * on the tile the simulation put it on.
 *
 * The ground is passed in rather than probed here for this module's own reason:
 * it reads no source and decides nothing about the world, it is given facts and
 * answers with boxes.
 */
function heapZ(at: TilePos, ground: number): number {
  return ground >= at.z && ground < at.z + 1 ? ground : at.z;
}

/**
 * How far off its tile's centre a body standing on a shared tile is drawn, in
 * tiles.
 *
 * **Derived from the body's own drawn width and the tile it may not leave**: the
 * widest ring a body's boxes fit inside its own tile on, which is half a tile
 * less half a body ({@link NPC_WIDTH_TILES}). Staying inside the tile is the
 * part that is not negotiable — the pick, the cone and the plate all read the
 * drawn point while every verb cites the tile (`witness/interact.ts`), so a
 * spread that crossed the boundary would be a client offering a press about the
 * neighbouring square.
 *
 * It is also, at that size, wide enough to do the job: two bodies on opposite
 * sides of the ring stand 0.56 tiles apart with 0.44 of shoulder between them,
 * which is daylight. Three or more share the ring and overlap a little, which is
 * three people on one square metre and reads as exactly that.
 */
const SHARED_TILE_SPREAD = 0.5 - NPC_WIDTH_TILES / 2;

/**
 * Where each body standing on a shared tile is drawn inside it: by figure, and
 * nothing at all for the bodies standing alone.
 *
 * **The local tier has no one-body-per-tile rule and never has**:
 * `sim_core::local`'s movement stage steps every walker along its path without
 * consulting any other presence, so two people at one bench — or somebody who
 * ran twelve tiles and stopped where a guard was standing — are two bodies on
 * one tile, and this client drew them through each other. The owner's session of
 * 2026-09-01: *"they are now just standing on the same place. Merged together."*
 *
 * **Presentation, and the simulation is not asked** (plan decision 8): the tile
 * is still what every verb cites, what the debug window reports and what the
 * watch measures. What moves is where the boxes are drawn inside it. The
 * sim-side answer — a walker that waits or swaps, DF's own rule (§7.1) — moves
 * every bootstrap position in every golden and is filed against the crowd
 * conversation instead.
 *
 * **By figure id, so it is stable**: the bodies on a tile are ranked by their
 * handle and spread evenly around {@link SHARED_TILE_SPREAD}, which gives the
 * same picture on every frame and in every session. A body standing alone gets
 * no entry and no offset, which is nearly every body in nearly every town — so a
 * street looks exactly as it did and only the merge is gone.
 *
 * A carried body is skipped: it is drawn in its carrier's arms rather than on
 * the ground ({@link carriedPlace}), and two babies held by two people are not
 * sharing anything.
 */
function spreadOnTiles(
  bodies: readonly PresentFigure[],
): ReadonlyMap<number, { readonly dx: number; readonly dy: number }> {
  const byTile = new Map<string, number[]>();

  for (const body of bodies) {
    if (body.heldBy !== null) {
      continue;
    }

    const key = `${body.at.x},${body.at.y},${body.at.z}`;
    const mates = byTile.get(key);

    if (mates === undefined) {
      byTile.set(key, [body.figure]);
    } else {
      mates.push(body.figure);
    }
  }

  const offsets = new Map<number, { readonly dx: number; readonly dy: number }>();

  for (const mates of byTile.values()) {
    if (mates.length < 2) {
      continue;
    }

    mates.sort((one, other) => one - other);

    for (const [index, figure] of mates.entries()) {
      const angle = (index / mates.length) * Math.PI * 2;

      offsets.set(figure, {
        dx: Math.cos(angle) * SHARED_TILE_SPREAD,
        dy: Math.sin(angle) * SHARED_TILE_SPREAD,
      });
    }
  }

  return offsets;
}

/**
 * How far a body may be from where the simulation put it before it is snapped
 * there rather than walked there.
 *
 * PROVISIONAL. Some of the ways a body moves are not walking: promotion
 * synthesises a whole roster onto the ground at once, a day boundary enrols
 * somebody who has just come of age, and a demotion and a re-promotion put the
 * same person somewhere else entirely. Gliding across a town square for those
 * would be the client inventing a journey nobody took.
 *
 * Two tiles is comfortably more than a walking step (which is one) and
 * comfortably less than the shortest thing that could be called a jump.
 */
export const WALK_SNAP_TILES = 2;

/** What a step with no snaps in it hands back, so the common path allocates nothing. */
const NO_SNAPS: readonly { readonly figure: number; readonly distance: number }[] = [];

/**
 * Turns a simulation cadence and a host step into tiles of drawn movement per
 * second.
 *
 * One tile per `ticksPerStep` host steps, which is what makes a drawn body
 * arrive exactly as the next real position lands. A cadence of zero would be a
 * division by zero and would mean the simulation moved a walker every tick,
 * which it does not; falling back to one step is the honest floor rather than a
 * crash in a renderer.
 */
function paceOf(ticksPerStep: number, stepSeconds: number): number {
  return 1 / (Math.max(1, ticksPerStep) * Math.max(stepSeconds, Number.EPSILON));
}

/**
 * Where a body is *drawn*, between the tiles the simulation walks it across.
 *
 * **Presentation, and the wave-1.5 graybox bar** (`docs/plans/phase-7-plan.md`):
 * the local tier moves a walker a whole tile at a time, once every
 * `sim_core::TICKS_PER_STEP` ticks, and a renderer that drew each of those the
 * moment it landed drew a crowd teleporting — which is what the wave-1 playtest
 * saw and said.
 *
 * Three rules, and every one of them is about staying on the right side of the
 * wall:
 *
 * - **It interpolates between two positions the simulation has already stated
 *   and never past the second.** Where a body goes next is the sim's business
 *   and arrives when it arrives; this is a drawn position catching up to a real
 *   one, which is the same thing {@link surfaceZ} does for footing and the same
 *   thing the host loop's own alpha does for the player.
 * - **The pace is the simulation's, forwarded rather than copied**
 *   (`sim_wasm::walk_ticks_per_step`). Moving at exactly one tile per sim step
 *   means the drawn position arrives as the next real one lands: a body walks
 *   at a constant speed and never rubber-bands.
 * - **A jump is snapped, not glided** ({@link WALK_SNAP_TILES}). Promotion and
 *   enrolment move people without walking them.
 * - **The height is the ground's, not the tile's** (chunk Sf). `x` and `y` are
 *   the simulation's; `z` is {@link surfaceZ} of that drawn point — the crowd's
 *   own footing rule, imported from `witness/crowd.ts` rather than re-derived,
 *   because a client with two footing rules is a client with two grounds.
 *
 * It is stepped on the simulation's clock like everything else in the witnessing
 * layer, so a night fast-forwarded through does not leave anybody mid-stride
 * (`witness/crowd.ts` makes the same call).
 *
 * # A body on a stair stood inside it
 *
 * The owner's playtest of 2026-08-30: *"the child was inside a stair; I was only
 * able to see his head."* A town's streets climb in steps since wave 1.6 and a
 * stair tile is an ordinary street column to whatever places a body on it, so
 * the simulation walks people onto flights all day. What this class handed back
 * was the tile's own `z` — the *bottom* of the flight's tile — while the mesher
 * draws treads up to a whole tile above it, so a body on the middle of a flight
 * was buried to the shoulders in the picture of the stairs it was climbing.
 *
 * The fix is the rule the rest of the client already walks on. {@link surfaceZ}
 * answers the tile's floor on flat ground — where nothing moves — and the slope
 * on a ramp or a flight, and it is the *same call* that puts the ambient crowd's
 * feet down (`witness/crowd.ts`), the same plane `player/slope.ts` stands the
 * player on, and the same call that already placed every tool, heap and market
 * board in the street (`witness/witness.ts`). Four surfaces, one function.
 *
 * Two things about *where* it is applied are decisions rather than convenience:
 *
 * - **The drawn position only.** The simulation's tile stays truth for
 *   everything else — who is where, what a walk costs, what the debug window's
 *   Bodies row reports. Nothing sends a height back across the wall and nothing
 *   here is hashed.
 * - **In {@link WalkSmoothing.placeOf} rather than in {@link bodyPieces}**, so
 *   every reader of a drawn body gets one answer: the prop, the interactable the
 *   `F` key addresses, the tracked body's ring (`Witnessing.drawnAt`) and the
 *   earshot the staged reaction measures. Two of those used to be the same
 *   number by accident; now they are the same number by construction.
 *
 * The smoothing is what makes a climb read as a climb. `x` and `y` glide between
 * two sim reads, and because the height is resolved from *them* rather than
 * interpolated between two tile floors, a body walking up a flight rises along
 * the same continuous line its feet are standing on — a twelfth of a tile per
 * step at the shipped cadence, never a half-tile jump at a tile boundary.
 */
export class WalkSmoothing {
  /** Where each body is drawn, by figure. Nothing here is hashed or recorded. */
  private readonly drawn = new Map<number, Drawn>();

  /**
   * Snaps taken since the last drain: who, and how far
   * ({@link WalkSmoothing.takeSnaps}).
   *
   * The wave-1.6 investigation's cheapest tripwire (item 6: *"a snap counter in
   * `WalkSmoothing.step`, expected 0 in ordinary play"*), and it costs nothing
   * to keep because the branch it lives in is the one that should never be
   * taken: an ordinary step is a body walking, and a walking body is under
   * {@link WALK_SNAP_TILES} by an order of magnitude. A drain rather than a sink
   * handed in, so this class keeps knowing nothing about who is watching.
   */
  private readonly snapped: { figure: number; distance: number }[] = [];

  /**
   * How fast a drawn body of a grown one catches up, in tiles of stepped time
   * per second — and the answer for any body whose stage nothing has resolved.
   */
  private readonly tilesPerSecond: number;

  /** The host's fixed step, or `null` for a session that does not smooth. */
  private readonly stepSeconds: number | null;

  /**
   * How many ticks a body of one life stage spends on a tile, or `null` for a
   * session that has nobody to ask (wave 2A).
   *
   * **The pace is per body since the gait table landed**
   * (`sim_wasm::walk_ticks_per_step_for_stage`): a child covers a tile in
   * fifteen ticks and a toddler in twenty-four, so a crowd of mixed ages
   * smoothed at one speed would draw the small ones rubber-banding. `null` is
   * the same answer the cadence itself gives — a session with no simulation
   * behind it — and it falls back on {@link WalkSmoothing.tilesPerSecond}.
   */
  private readonly ticksForStage: ((stage: number) => number) | null;

  /** Tiles per second by stage id, resolved on first use. Four entries, ever. */
  private readonly paces = new Map<number, number>();

  /** The ground the drawn heights are read off, or `null` for a session with none. */
  private readonly terrain: TerrainSampler | null;

  /**
   * `stepSeconds` is the host's fixed step and `ticksPerStep` the simulation's
   * walking cadence, so the pace is one tile per `ticksPerStep` steps — a
   * number the client is *given* rather than one it knows.
   *
   * **`null` for either means no smoothing at all**, and it is an answer rather
   * than a degradation: a session built without a simulation behind it has no
   * cadence to be told and nobody who walks — the fixture's town is promoted and
   * its people stand exactly where it says they do, for ever
   * (`world/fixture.ts`). Snapping to a position that never moves is what those
   * sessions were doing anyway.
   *
   * **`terrain` is optional and `null` means "draw them where the simulation
   * says"**, which is the same kind of answer: a check that fabricates a roster
   * and no ground has no surface to resolve against, and inventing one would be
   * worse than reporting the tile. Every playing session has terrain
   * (`witness/witness.ts` hands it its own), so the flat answer is scaffolding's
   * and not a player's.
   */
  constructor(
    ticksPerStep: number | null,
    stepSeconds: number | null,
    terrain: TerrainSampler | null = null,
    ticksForStage: ((stage: number) => number) | null = null,
  ) {
    this.terrain = terrain;
    this.ticksForStage = ticksForStage;
    this.stepSeconds = stepSeconds;

    if (ticksPerStep === null || stepSeconds === null) {
      this.tilesPerSecond = Number.POSITIVE_INFINITY;
      this.stepSeconds = null;

      return;
    }

    this.tilesPerSecond = paceOf(ticksPerStep, stepSeconds);
  }

  /**
   * How fast a body of one life stage catches up, in tiles per second.
   *
   * Memoised on the stage id, because the answer is a row of a four-row table
   * and the question is asked once per body per step: a call across the wasm
   * boundary per body per step for a number that never moves would be the cost
   * decision 11 is about.
   *
   * The grown pace for a body whose stage nothing resolved, and for every
   * session with no simulation behind it to ask — which keeps a fixture's
   * bodies snapping exactly as they always did.
   */
  private tilesPerSecondFor(stage: number): number {
    const resolve = this.ticksForStage;
    const seconds = this.stepSeconds;

    if (resolve === null || seconds === null) {
      return this.tilesPerSecond;
    }

    const known = this.paces.get(stage);

    if (known !== undefined) {
      return known;
    }

    const pace = paceOf(resolve(stage), seconds);
    this.paces.set(stage, pace);

    return pace;
  }

  /**
   * Walks every drawn body one step towards where the simulation says it is,
   * and forgets the ones that are no longer there.
   *
   * Forgetting matters: a demoted town's roster stops being answered for, and a
   * map that kept them would hand a stale position back if the same figure were
   * ever enrolled again — which is exactly the case {@link WALK_SNAP_TILES} is
   * for, arriving as a glide instead.
   */
  step(bodies: readonly PresentFigure[], stepSeconds: number): void {
    const present = new Set<number>();

    for (const body of bodies) {
      present.add(body.figure);

      const target = centreOf(body);
      const here = this.drawn.get(body.figure);

      if (here === undefined) {
        this.drawn.set(body.figure, this.footing(target));

        continue;
      }

      const budget = this.tilesPerSecondFor(body.stage) * stepSeconds;
      const dx = target.x - here.x;
      const dy = target.y - here.y;
      const dz = target.z - here.z;
      const distance = Math.hypot(dx, dy, dz);

      if (distance > WALK_SNAP_TILES || distance <= budget) {
        if (distance > WALK_SNAP_TILES) {
          // Only the *jump* is worth writing down. The other half of this branch
          // is a body arriving, which is every body every step
          // ({@link WalkSmoothing.snapped}).
          this.snapped.push({ figure: body.figure, distance });
        }

        this.drawn.set(body.figure, this.footing(target));

        continue;
      }

      const share = budget / distance;

      this.drawn.set(
        body.figure,
        this.footing({
          x: here.x + dx * share,
          y: here.y + dy * share,
          z: here.z + dz * share,
        }),
      );
    }

    for (const figure of [...this.drawn.keys()]) {
      if (!present.has(figure)) {
        this.drawn.delete(figure);
      }
    }
  }

  /**
   * Where a body should be drawn this frame: the smoothed point, standing on the
   * ground under it.
   *
   * The simulation's own answer for anybody this has not seen yet — put through
   * the same footing — which is what makes a body appear where it belongs on the
   * first frame rather than sliding in from wherever the previous occupant of
   * the map entry was.
   *
   * **A fresh object rather than the stored record**, and it earns its
   * allocation: {@link Drawn} carries the sim-space `z` the interpolation runs
   * on beside the height the body is drawn at, and callers spread this into a
   * {@link SceneProp} — so handing back the record itself would put a field on
   * the props that the renderer never asked for and the type system would not
   * catch through a spread.
   */
  placeOf(body: PresentFigure): { readonly x: number; readonly y: number; readonly z: number } {
    const here = this.drawn.get(body.figure) ?? this.footing(centreOf(body));

    return { x: here.x, y: here.y, z: here.surface };
  }

  /**
   * Takes the snaps since the last call, emptying the list.
   *
   * Drained by {@link Witnessing} right after `step`, and by nothing else. It
   * exists for the debug window (`dev/debuglog.ts`) and answers an empty array
   * in every ordinary step of every ordinary session — which is the reading the
   * investigation wants: **any** entry at all means a body moved further than a
   * walk between two reads, and the wave-1.6 note ruled that out for ordinary
   * play (item 6, `WALK_SNAP_TILES` is not the teleport's cause).
   */
  takeSnaps(): readonly { readonly figure: number; readonly distance: number }[] {
    if (this.snapped.length === 0) {
      // The common path allocates nothing: a session that never snaps never
      // makes an array for the fact that it did not.
      return NO_SNAPS;
    }

    return this.snapped.splice(0, this.snapped.length);
  }

  /**
   * Puts a drawn point on the ground, keeping the simulation's own height beside
   * it.
   *
   * One {@link surfaceZ} probe per body per **step**, not per frame: this is
   * called from {@link WalkSmoothing.step} where the drawn position actually
   * moves, and the frames in between reuse the answer exactly as they already
   * reuse the position. That is the crowd's discipline for the same reason —
   * a probe is a handful of queries across the wasm boundary and the ground does
   * not move between them (`witness/crowd.ts`'s `settle`).
   *
   * **The sim-space `z` is kept and never overwritten by the surface.** It is
   * what the next step interpolates from and what {@link WALK_SNAP_TILES}
   * measures against, and feeding a lifted height back into either would make
   * the rise of a staircase read as distance travelled — a body climbing would
   * snap.
   */
  private footing(place: { x: number; y: number; z: number }): Drawn {
    return {
      ...place,
      surface:
        this.terrain === null ? place.z : surfaceZ(this.terrain, place.x, place.y),
    };
  }
}

/** One body's drawn position: where the simulation has it, and where it stands. */
interface Drawn {
  /**
   * The smoothed position in the simulation's own space — a tile centre, or a
   * point between two of them ({@link WalkSmoothing.step}).
   */
  x: number;
  y: number;
  z: number;

  /**
   * The height the body's feet are *drawn* at: {@link surfaceZ} of `x, y`.
   *
   * Equal to `z` on flat ground and above it on a ramp or a flight of stairs,
   * which is the whole of chunk Sf.
   */
  surface: number;
}

/**
 * The tile centre of a body's position.
 *
 * Tile centre, like every other prop the client places on a tile: the
 * simulation's position is a tile and a body standing on its corner would be a
 * body standing in the street's gutter.
 */
function centreOf(body: PresentFigure): { x: number; y: number; z: number } {
  return { x: body.at.x + 0.5, y: body.at.y + 0.5, z: body.at.z };
}

/**
 * Which way each body is turned, derived from where it has been.
 *
 * **No facing from the simulation, and there will not be one.** Which way a body
 * is turned is a rendering choice the sim has never held an opinion about,
 * exactly as which way a ramp faces is. Until item 6 that meant `facing: 0` —
 * a whole town looking north, which reads as a shop display rather than a
 * street.
 *
 * The answer is the one {@link WalkSmoothing} is already built on and it is the
 * V-A rule stated for a rotation: **presentation between two positions the
 * simulation has stated, and never a third one it has not.** A body that has
 * moved between two sim reads is facing the way it moved; a body that has not
 * keeps the way it was facing. Nothing here invents a heading, nothing turns
 * towards anything, and a session that drew every facing at zero would be the
 * same session with a worse picture.
 *
 * The two reads are `readLocal`'s, so they are a scene tick apart
 * (`witness/witness.ts`) — which is at least one sim tick, and the sim moves a
 * walker one tile every `walk_ticks_per_step` of them. So most reads show no
 * displacement at all and the kept facing is what most bodies are drawn with,
 * which is the point: a body stands still facing wherever it last walked.
 */
export class BodyFacing {
  /** Where each body was, and which way it is turned, by figure. Not hashed, not recorded. */
  private readonly turned = new Map<number, { x: number; y: number; facing: number }>();

  /**
   * Takes a fresh read of the presence list and turns whoever moved.
   *
   * Bodies the list no longer holds are forgotten, for {@link WalkSmoothing}'s
   * reason: a demoted town stops being answered for, and a facing kept for a
   * figure that comes back later would be a body turned by a walk it took in
   * another town.
   */
  update(bodies: readonly PresentFigure[]): void {
    const present = new Set<number>();

    for (const body of bodies) {
      present.add(body.figure);

      const was = this.turned.get(body.figure);

      if (was === undefined) {
        // The first read a body appears in has no displacement behind it, so
        // there is nothing to face: north, until it walks.
        this.turned.set(body.figure, { x: body.at.x, y: body.at.y, facing: 0 });

        continue;
      }

      const dx = body.at.x - was.x;
      const dy = body.at.y - was.y;

      if (dx === 0 && dy === 0) {
        continue;
      }

      // Yaw 0 faces +y and positive yaw turns left, so the heading of a movement
      // vector is `atan2(-dx, dy)` — the same conversion `witness/crowd.ts` and
      // the refugee columns use, and the inverse of the controller's
      // `(-sin, cos)` basis.
      this.turned.set(body.figure, { x: body.at.x, y: body.at.y, facing: Math.atan2(-dx, dy) });
    }

    for (const figure of [...this.turned.keys()]) {
      if (!present.has(figure)) {
        this.turned.delete(figure);
      }
    }
  }

  /** Which way one body is turned, in radians. Zero for anybody never seen to move. */
  facingOf(figure: number): number {
    return this.turned.get(figure)?.facing ?? 0;
  }
}

/**
 * How high above a carrier's feet the body in their arms is drawn, in tiles.
 *
 * **Presentation, and the only number this file authors about being carried.**
 * The simulation says *who* is holding somebody and nothing about where that
 * puts them (`sim_core::local`'s `Presence::held_by`) — a held body's sim tile
 * is the tile it was lifted from — so where a baby rides is a drawing decision
 * like a facing is.
 *
 * Chest height on the shipped adult, whose shoulders are at 1.32 tiles
 * (`render/props.ts`'s `NPC_NECK_TILES`): high enough that the carried body
 * clears its carrier's legs and low enough to read as arms rather than as a
 * shoulder lift. PROVISIONAL, and judged on a screenshot like every other
 * number of its kind.
 */
export const CARRY_HEIGHT_TILES = 0.95;

/**
 * Where a body somebody is carrying is drawn: at whoever is holding it.
 *
 * **Rider 2's held relation, on the drawn side** (Phase 7 plan, 2026-08-28).
 * The simulation moves a carried body with its carrier while the carrier is a
 * *sim* body, and cannot when the carrier is the **player** — who has no
 * presence record and never will (`docs/ARCHITECTURE.md`) — so the tile the
 * simulation reports for a body in the player's arms is where it was picked up.
 * Drawing it there would leave a baby lying in the street the player is walking
 * away from with it.
 *
 * So the resolution is here, where the client already knows both bodies: a held
 * body's drawn point is its carrier's drawn point, raised
 * ({@link CARRY_HEIGHT_TILES}). `carrierAt` is what the caller knows and this
 * module does not — where the *player* is — and `null` from it means "nobody I
 * can place", which leaves the body drawn where the simulation says it is. That
 * is the honest fallback rather than a hidden body: rider 1 is that a client
 * draws every body the sim has.
 */
function carriedPlace(
  body: PresentFigure,
  walk: WalkSmoothing,
  bodies: readonly PresentFigure[],
  carrierAt: (carrier: number) => { readonly x: number; readonly y: number; readonly z: number } | null,
): { readonly x: number; readonly y: number; readonly z: number } {
  const here = walk.placeOf(body);
  const carrier = body.heldBy;

  if (carrier === null) {
    return here;
  }

  const embodied = bodies.find((other) => other.figure === carrier);
  const at = embodied === undefined ? carrierAt(carrier) : walk.placeOf(embodied);

  if (at === null) {
    return here;
  }

  return { x: at.x, y: at.y, z: at.z + CARRY_HEIGHT_TILES };
}

/**
 * Where a body stands, what it is called, how big it is and who is carrying it
 * — joined from the lists the simulation answers with.
 *
 * The positions are the local tier's ({@link WitnessSource.presentFigures}) and
 * the names are the roster's — two reads on two cadences, because a name is a
 * string that crosses per call and a position moves every tick. A figure the
 * roster has no name for is still drawn: the body is what the simulation put
 * there, and a nameless plate is better than a missing person.
 *
 * `walk` is where the body is *drawn*, which since wave 1.5 is not always where
 * the simulation last put it: a walker glides across the tile it was moved over,
 * and since chunk Sf its **height** is the ground under that glide rather than
 * the tile's own floor, so a body on a flight of stairs stands on the treads
 * ({@link WalkSmoothing}). `facing` is which way it is turned, which since item
 * 6 is where it last walked ({@link BodyFacing}).
 *
 * **`stageWord` and `scale` are handed in exactly as `nameOf` is**, and for the
 * same reason: this module reads no source and decides nothing about the world,
 * it is given facts and answers with boxes. Both come off the simulation's own
 * body plan since wave 2A — the word out of the stage table's vocabulary and
 * the height off its size curve — which is what deleted the two authored child
 * scales and the client-side `(child)` tag this function used to compose
 * (`render/props.ts`).
 */
export function bodyPieces(
  bodies: readonly PresentFigure[],
  nameOf: (figure: number) => string,
  stageWord: (stage: number) => string,
  scaleOf: (stage: number) => number,
  walk: WalkSmoothing,
  facing: BodyFacing,
  carrierAt: (carrier: number) => { readonly x: number; readonly y: number; readonly z: number } | null = () => null,
): readonly SceneProp[] {
  // Who is standing on somebody else's tile, worked out once for the list
  // rather than per body: two bodies on one tile are drawn apart inside it
  // ({@link spreadOnTiles}), and an ordinary town produces an empty map.
  const apart = spreadOnTiles(bodies);

  return bodies.map((body) => {
    const name = nameOf(body.figure);
    const word = stageWord(body.stage);
    // A body the roster has no name for gets the word on its own rather than a
    // leading space: the plate still says the one thing the client knows about
    // them, which is this function's rule about a nameless body applied to the
    // stage table's own vocabulary. A grown body's word is empty, so an adult's
    // plate is their name and nothing else.
    const label = word === "" ? name : name === "" ? `(${word})` : `${name} (${word})`;
    const place = carriedPlace(body, walk, bodies, carrierAt);
    const shift = apart.get(body.figure);

    return {
      id: `npc:${body.figure}`,
      kind: "npc" as const,
      // The drawn point, moved inside its own tile where somebody else is
      // standing on it. Everything downstream reads *this* — the prop, the
      // subject the `F` key addresses, the plate over its head — so the pick,
      // the cone and the name stay on the body a player is looking at
      // (`witness/witness.ts`'s `collectCrowd`).
      x: place.x + (shift?.dx ?? 0),
      y: place.y + (shift?.dy ?? 0),
      z: place.z,
      facing: facing.facingOf(body.figure),
      label,
      palette: body.figure,
      stage: stageOf(word),
      scale: scaleOf(body.stage),
    };
  });
}

/**
 * The geometry cache's key for a stage, from the word the simulation gave it.
 *
 * **The word is the simulation's and the union is the renderer's**
 * ({@link BodyStage}): a stage id crosses the wall as a number and comes back as
 * a word, and what `render/props.ts` wants is a short label it can put in a
 * cache key. An unrecognised word — a race whose table names a fifth row —
 * draws grown, which is the same safe direction the exports themselves take.
 */
function stageOf(word: string): BodyStage {
  switch (word) {
    case "baby":
      return "baby";
    case "toddler":
      return "toddler";
    case "child":
      return "child";
    default:
      return "adult";
  }
}

/**
 * How far round a tool has rocked at one instant.
 *
 * A sine of the stepped clock, offset by the site's own coordinates so two
 * workers are never in time. Presentation state and nothing else: it is not
 * hashed, not recorded, and a session that drew it frozen would be the same
 * session (`witness/crowd.ts`'s note about client randomness applies word for
 * word).
 */
function swing(site: TilePos, phase: number): number {
  const offset = (site.x * 0.37 + site.y * 0.61 + site.z * 0.13) % (Math.PI * 2);

  return Math.sin((phase / TOIL_SWING_SECONDS) * Math.PI * 2 + offset) * TOIL_SWING_RADIANS;
}
